import React, { useCallback, useEffect, useState } from "react";
import InputUnderline from "../../components/controls/input/InputUnderline";
import Button from "../../components/controls/button/Button";
import { AddIconSvg } from "../../modules/default-svg";
import { ColumnConfigProvider } from "../../components/controls/tables/ColumnConfigProvider";
import { calculatePageSize } from "../../helpers/utils";
import Table from "../../components/controls/tables/Table";
import RichTextEditor from "../../components/richTextEditor/RichTextEditor";

const Roles = (props) => {
  const {
    allRoles,
    allPermissions,
    getRolesRequest,
    loading,
    roleLoading,
    createRoleRequest,
    updateRoleRequest,
    deleteRoleRequest,
    updaterolePermissions,
    permissionLoading,
    pagination,
    error,
  } = props;

  const [selectedRow, setSelectedRow] = useState({});
  const [isEdit, setIsEdit] = useState(false);
  const [isCreate, setIsCreate] = useState(false);
  const [isCreateNewRow, setIsCreateNewRow] = useState(false);
  const [deleteId, setDeletedId] = useState(null);
  const [pageNum, setPageNum] = useState(1);
  const [perPageItems] = useState(calculatePageSize());
  const [data, setData] = useState([]);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [permissions, setPermissions] = useState([]);
  const [formErrors, setFormErrors] = useState({
    name: "",
  });
  const [tableSearch, setTableSearch] = useState(null);
  const fetchData = (pageNumber, pageSize, searchTerm, forceClearSearch=false) => {
    const payload = { page: pageNumber ?? pageNum, size: pageSize ?? perPageItems, search: forceClearSearch ? null : (searchTerm ?? tableSearch) };
    getRolesRequest(payload);
  };
  useEffect(() => {
    if (allRoles && allRoles.length > 0) {
      if (isCreateNewRow) {
        setData(
          allRoles.sort((a, b) => {
            return b.isFlagged - a.isFlagged;
          })
        );
        setIsCreateNewRow(false);
      } else {
        setData((prevData) => {
          const newMap = new Map(allRoles.map((item) => [item.id, item]));

          const updatedPrevData = prevData.map((item) =>
            newMap.has(item.id) ? newMap.get(item.id) : item
          );

          const prevDataIds = new Set(prevData.map((item) => item.id));
          const newItems = allRoles.filter(
            (shift) => !prevDataIds.has(shift.id)
          );

          return [...updatedPrevData, ...newItems]
            .filter((item) => item.id !== deleteId)
            .sort((a, b) => b.isFlagged - a.isFlagged);
        });
      }
      setIsEdit(false);
      setIsCreate(false);
      setIsCreateNewRow(false);
      setSelectedRow({});
    } else if (allRoles && allRoles.length === 0 && pageNum === 1) {
      setData([]);
      setIsEdit(false);
      setIsCreate(false);
      setIsCreateNewRow(false);
      setSelectedRow({});
    }
  }, [allRoles]);

  const handleAddRow = useCallback(() => {
    setIsCreateNewRow(true);
    fetchData(pagination.TotalPages ?? 1, perPageItems > 10 ? perPageItems - 5 : perPageItems - 3);
  }, []);

  const handleCancelAddRow = () => {
    setIsCreateNewRow(false);
    fetchData(1, perPageItems);
  };

  useEffect(() => {
    fetchData();
  }, [pageNum, perPageItems]);

  useEffect(() => {
    if (Array.isArray(allPermissions)) {
      setPermissions(allPermissions);
    }
  }, [allPermissions]);

  const handleDelete = (selectedRow) => {
    const id = selectedRow?.id;
    deleteRoleRequest({ id: id });
    setDeletedId(id);
  };

  const handleSavePermissions = () => {
    const row = JSON.parse(localStorage.getItem("selectedRow"));
    const roleId = row.id;
    const uniqueArray = [...new Set(permissions)];
    updaterolePermissions({ id: roleId, permissions: uniqueArray });
  };

  const handlePermissions = (add, arr) => {
    if (add) {
      arr.map((permission) => setPermissions((prev) => [...prev, permission]));
    } else {
      const filteredPermissions = permissions.filter(
        (permission) => !arr.includes(permission)
      );
      setPermissions(filteredPermissions);
    }
  };

  const handleSave = () => {
    const errors = {};

    if (!name) errors["name"] = `Name field is required.`;
    setFormErrors(errors);

    if (Object.keys(errors).length) {
      return;
    }

    if (Object.keys(selectedRow).length > 0)
      updateRoleRequest({ id: selectedRow.id, name, description });
    else createRoleRequest({ payload: { name, description } });
  };

  const handleCancel = () => {
    setFormErrors({});
    setIsEdit(false);
  };

  useEffect(() => {
    if (Object.keys(selectedRow).length > 0) {
      setIsEdit(true);
      setName(selectedRow.name);
      setDescription(selectedRow.description);
    } else {
      setName("");
      setDescription("");
    }
  }, [selectedRow, isEdit]);

  useEffect(() => {
    if (error) {
      setFormErrors({ name: error });
    } else {
      setFormErrors({});
    }
  }, [error]);

  useEffect(() => {
    if (name) {
      setFormErrors({});
    }
  }, [name]);

  const formBody = () => {
    return (
      <>
        <div className="d-flex align-items-start w-100 mb-3">
          <InputUnderline
            inputUnderlinewd="w-100"
            isRequired
            placeholder="Role name"
            value={name}
            handleChange={setName}
            error={formErrors.name}
          />
        </div>
        <div className="d-flex align-items-start w-100">
          <RichTextEditor
            label="Description"
            content={description}
            onChange={setDescription}
          />
        </div>
      </>
    );
  };

  const handleCreate = () => {
    setIsCreate(true);
  };

  const handleSearch = (filteredData) => {
    setData(filteredData);
  };

  const handleSearchTrigger = (searchTerm) => {
    if((!tableSearch && !searchTerm) || (tableSearch === searchTerm)) return;
  
    let pageNumber = pageNum;
    if(!tableSearch || (tableSearch && !searchTerm) || (tableSearch !== searchTerm)){
      setData([]); //clear existing data
      pageNumber = 1; //reset to first page
      setPageNum(1);
    }
      
    setTableSearch(searchTerm);
    fetchData(pageNumber, perPageItems, searchTerm, !searchTerm);
  };

  return (
    <ColumnConfigProvider tableName="Role">
      <div className="listingScreen position-relative">
        <div className="db_Subheader d-flex justify-content-between align-items-center mb-4">
          <h4>Roles</h4>
          <Button
            btnClass="d_btn d_btn_primary"
            icon={<AddIconSvg />}
            label="Add Role"
            clickHandler={handleCreate}
          />
        </div>

        <Table
          type="Role"
          rows={data || []}
          searchHandler={handleSearch}
          body={formBody()}
          saveBtnLabel="Save"
          cancelBtnLabel="Cancel"
          setPageNum={setPageNum}
          perPageItems={perPageItems}
          setIsCreate={setIsCreate}
          handleSave={() => handleSave()}
          handleDelete={(selectedRow) => handleDelete(selectedRow)}
          handleCancel={() => handleCancel()}
          setIsEdit={setIsEdit}
          loading={loading}
          modalLoading={roleLoading || permissionLoading}
          isCreate={isCreate}
          pagination={pagination}
          setData={setData}
          setSelectedRow={setSelectedRow}
          handleSavePermissions={handleSavePermissions}
          handlePermissions={handlePermissions}
          permissions={permissions}
          permissionTitle={selectedRow.name}
          handleAddRow={handleAddRow}
          handleCancelAddRow={handleCancelAddRow}
          triggerSearch={handleSearchTrigger}
        />
      </div>
    </ColumnConfigProvider>
  );
};

export default Roles;
